import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { db } from '../firebaseConfig';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { collection, addDoc, doc, setDoc, deleteDoc, getDocs, getDoc } from "firebase/firestore";
import './TradeEntryForm.css';
import MDButton from "components/MDButton";
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from 'components/MDTypography';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Container, IconButton, Paper, Grid, TextField, FormControlLabel, Chip, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';

export function calculatePipIncrement(symbol) {
  const pipIncrement = {
    'AUDCAD': 0.0001, 'AUDCHF': 0.0001, 'AUDJPY': 0.01, 'AUDNZD': 0.0001, 'AUDUSD': 0.0001,
    'CADCHF': 0.0001, 'CADJPY': 0.01, 'CHFJPY': 0.01, 'EURAUD': 0.0001, 'EURCAD': 0.0001,
    'EURCHF': 0.0001, 'EURGBP': 0.0001, 'EURNZD': 0.0001, 'EURUSD': 0.0001, 'GBPAUD': 0.0001,
    'GBPCAD': 0.0001, 'GBPCHF': 0.0001, 'GBPJPY': 0.01, 'GBPNZD': 0.0001, 'GBPUSD': 0.0001,
    'NZDCAD': 0.0001, 'NZDCHF': 0.0001, 'NZDJPY': 0.01, 'NZDUSD': 0.0001, 'USDCAD': 0.0001,
    'USDCHF': 0.0001, 'USDJPY': 0.01, 'AUDGBP': 0.0001, 'AUDHKD': 0.0001, 'AUDSGD': 0.0001,
    'CHFHUF': 0.01, 'CHFPLN': 0.0001, 'EURAED': 0.0001, 'EURCZK': 0.0001, 'EURDKK': 0.0001,
    'EURHKD': 0.0001, 'EURHUF': 0.01, 'GBPCZK': 0.0001, 'GBPDKK': 0.0001, 'GBPTRY': 0.0001,
    'HKDJPY': 0.01, 'NZDSGD': 0.0001, 'SGDJPY': 0.01, 'USDCNH': 0.0001, 'USDHKD': 0.0001,
    'USDSGD': 0.0001, 'USDTRY': 0.0001, 'USDZAR': 0.0001, 'ZARJPY': 0.01, 'XAUUSD': 0.01,
    'XAGUSD': 0.01, 'XPTUSD': 0.01, 'XPDUSD': 0.01, 'WTIUSD': 0.01, 'BRENTUSD': 0.01,
    'COPPERUSD': 0.0001, 'NGASUSD': 0.01, 'COFFEEUSD': 0.01, 'SUGARUSD': 0.0001, 'COTTONUSD': 0.01,
    'US30': 0.01, 'SPX500': 0.01, 'NAS100': 0.01, 'UK100': 0.01, 'GER40': 0.01, 
        // New Additions
        'EURJPY': 0.01,    // Euro/Japanese Yen
        'EURPLN': 0.0001,  // Euro/Polish Zloty
        'EURTRY': 0.0001,  // Euro/Turkish Lira
        'GBPHKD': 0.0001,  // British Pound/Hong Kong Dollar
        'USDPLN': 0.0001,  // US Dollar/Polish Zloty
        'USDMXN': 0.0001,  // US Dollar/Mexican Peso
        'AUDTRY': 0.0001,  // Australian Dollar/Turkish Lira
        'JPYHKD': 0.01,    // Japanese Yen/Hong Kong Dollar
        'CHFSGD': 0.0001   // Swiss Franc/Singapore Dollar
  }[symbol] || 0.0001;
  return pipIncrement;
}

const symbolOptions = [
  'AUDCAD', 'AUDCHF', 'AUDJPY', 'AUDNZD', 'AUDUSD', 'CADCHF', 'CADJPY', 'CHFJPY', 'EURAUD', 'EURCAD',
  'EURCHF', 'EURGBP', 'EURNZD', 'EURUSD', 'GBPAUD', 'GBPCAD', 'GBPCHF', 'GBPJPY', 'GBPNZD', 'GBPUSD',
  'NZDCAD', 'NZDCHF', 'NZDJPY', 'NZDUSD', 'USDCAD', 'USDCHF', 'USDJPY', 'AUDGBP', 'AUDHKD', 'AUDSGD',
  'CHFHUF', 'CHFPLN', 'EURAED', 'EURCZK', 'EURDKK', 'EURHKD', 'EURHUF', 'GBPCZK', 'GBPDKK', 'GBPTRY',
  'HKDJPY', 'NZDSGD', 'SGDJPY', 'USDCNH', 'USDHKD', 'USDSGD', 'USDTRY', 'USDZAR', 'ZARJPY', 'XAUUSD',
  'XAGUSD', 'XPTUSD', 'XPDUSD', 'WTIUSD', 'BRENTUSD', 'COPPERUSD', 'NGASUSD', 'COFFEEUSD', 'SUGARUSD',
  'COTTONUSD', 'US30', 'SPX500', 'NAS100', 'UK100', 'GER40',
    // New Additions
  'EURJPY', 'EURPLN', 'EURTRY', 'GBPHKD', 'USDPLN', 'USDMXN', 'AUDTRY', 'JPYHKD', 'CHFSGD'
];

const exchangeRateSymbols = {
  'GBPJPY': 'USDJPY', 'EURGBP': 'GBPUSD', 'AUDJPY': 'USDJPY', 'AUDUSD': 'USDJPY', 'AUDCAD': 'USDCAD',
  'AUDCHF': 'USDCHF', 'AUDNZD': 'NZDUSD', 'CADJPY': 'USDJPY', 'CADCHF': 'USDCHF', 'CHFJPY': 'USDJPY',
  'EURAUD': 'AUDUSD', 'EURCAD': 'USDCAD', 'EURCHF': 'USDCHF', 'EURNZD': 'NZDUSD', 'EURUSD': 'EURUSD',
  'GBPAUD': 'AUDUSD', 'GBPCAD': 'USDCAD', 'GBPCHF': 'USDCHF', 'GBPNZD': 'NZDUSD', 'GBPUSD': 'GBPUSD',
  'NZDJPY': 'USDJPY', 'NZDUSD': 'NZDUSD', 'NZDCAD': 'USDCAD', 'NZDCHF': 'USDCHF', 'USDCAD': 'USDCAD',
  'USDCHF': 'USDCHF', 'USDJPY': 'USDJPY', 'AUDGBP': 'GBPUSD', 'AUDHKD': 'HKDUSD', 'AUDSGD': 'SGDUSD',
  'CHFHUF': 'USDHUF', 'CHFPLN': 'PLNUSD', 'EURAED': 'AEDUSD', 'EURCZK': 'CZKUSD', 'EURDKK': 'DKKUSD',
  'EURHKD': 'HKDUSD', 'EURHUF': 'USDHUF', 'GBPCZK': 'CZKUSD', 'GBPDKK': 'DKKUSD', 'GBPTRY': 'TRYUSD',
  'HKDJPY': 'USDJPY', 'NZDSGD': 'SGDUSD', 'SGDJPY': 'USDJPY', 'USDCNH': 'CNYUSD', 'USDHKD': 'HKDUSD',
  'USDSGD': 'SGDUSD', 'USDTRY': 'TRYUSD', 'USDZAR': 'ZARUSD', 'ZARJPY': 'USDJPY', 'US30': 'USD',
  'SPX500': 'USD', 'NAS100': 'USD', 'UK100': 'GBPUSD', 'GER40': 'EURUSD',
    // New Additions
    'EURJPY': 'USDJPY',    // Euro/Japanese Yen -> USD/JPY
    'EURPLN': 'PLNUSD',    // Euro/Polish Zloty -> PLN/USD
    'EURTRY': 'TRYUSD',    // Euro/Turkish Lira -> TRY/USD
    'GBPHKD': 'HKDUSD',    // British Pound/Hong Kong Dollar -> HKD/USD
    'USDPLN': 'PLNUSD',    // US Dollar/Polish Zloty -> PLN/USD
    'USDMXN': 'MXNUSD',    // US Dollar/Mexican Peso -> MXN/USD
    'AUDTRY': 'TRYUSD',    // Australian Dollar/Turkish Lira -> TRY/USD
    'JPYHKD': 'HKDUSD',    // Japanese Yen/Hong Kong Dollar -> HKD/USD
    'CHFSGD': 'SGDUSD'     // Swiss Franc/Singapore Dollar -> SGD/USD
};

// Contract sizes for commission calculation (expand as needed)
const contractSizes = {
  'XAUUSD': 100, 'XAGUSD': 5000, 'XPTUSD': 50, 'XPDUSD': 100, 'WTIUSD': 1000, 'BRENTUSD': 1000,
  'COPPERUSD': 25000, 'NGASUSD': 10000, 'COFFEEUSD': 37500, 'SUGARUSD': 112000, 'COTTONUSD': 50000,
  'US30': 5, 'SPX500': 50, 'NAS100': 20, 'UK100': 10, 'GER40': 25,
  // Default forex pairs: 100,000 units
};

// Helper to get contract size (defaults to 100,000 for forex)
const getContractSize = (symbol) => contractSizes[symbol] || 100000;

const API_KEY = process.env.REACT_APP_API_KEY;
const BASE_URL = process.env.REACT_APP_BASE_URL;

export const getBaseCurrency = (tradeData) => {
  return typeof tradeData === 'string' ? tradeData.slice(0, 3) : tradeData.symbol.slice(0, 3);
};

export const getQuoteCurrency = (tradeData) => {
  return typeof tradeData === 'string' ? tradeData.slice(-3) : tradeData.symbol.slice(-3);
};

export const getExchangeRateSymbol = (tradeData) => {
  const symbol = typeof tradeData === 'string' ? tradeData : tradeData.symbol;
  const baseCurrency = getBaseCurrency(symbol);
  return exchangeRateSymbols[symbol] || `${baseCurrency}USD`;
};

export const getExchangeRate = (tradeData) => {
  const symbol = typeof tradeData === 'string' ? tradeData : tradeData.symbol;
  const baseCurrency = getBaseCurrency(symbol);
  const quoteCurrency = getQuoteCurrency(symbol);
  if (baseCurrency === 'USD') return parseFloat(tradeData.exit) || 1;
  if (quoteCurrency === 'USD') return 1;
  return parseFloat(tradeData.exchangeRate) || 1;
};

const fetchExchangeRate = async (symbol) => {
  try {
    const formattedSymbol = `${symbol.slice(0, 3)}/${symbol.slice(3)}`; // e.g., "USD/CAD"
    const params = {
      apikey: API_KEY,
      symbol: formattedSymbol,
    };

    const response = await axios.get(`${BASE_URL}/quote`, { params });
    if (response.data.code && response.data.message) {
      console.error('API Error:', response.data.code, response.data.message);
      return null;
    }

    return response.data.high ? parseFloat(response.data.high) : null; // Use latest high
  } catch (error) {
    console.error('Error fetching exchange rate from TwelveData:', error);
    return null;
  }
};

const Modal = ({ isOpen, onClose, title, children }) => {
  const theme = useTheme();
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div className="modal" style={{ backgroundColor: theme.palette.background.default || '#bac7db', padding: '20px', borderRadius: '8px', maxWidth: '500px', width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MDTypography variant="h4" component="h2" style={{ margin: 0, fontWeight: 'bold', textAlign: 'center', alignItems: 'center' }}>
            {title}
          </MDTypography>

        </div>
        <div className="modal-content" style={{ marginTop: '16px' }}>{children}</div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const TradeEntryForm = ({ userID, onTradeSubmit }) => {
  const [tradeData, setTradeData] = useState({
    date: new Date().toISOString().split('T')[0],
    entryTime: '',
    exitDate: '',
    exitTime: '',
    symbol: '',
    entry: '',
    exit: '',
    status: 'Open',
    sizeType: 'lot',
    lotSizeEntry: '',
    shortLong: 'Long',
    returns: '',
    returnPercentage: '',
    leverage: '',
    sl: '',
    tp: '',
    riskAmount: '',
    comments: '',
    baseCurrency: '',
    exchangeRate: '',
    tradeConfidence: 'Medium',
    tags: [],
    commissionType: 'off',
    commissionValue: '',
    tradeAccountID: 'default', // New field for trade account
  });

  const [tradeAccounts, setTradeAccounts] = useState([{ id: 'default', name: 'Default' }]);
  const [showExchangeRateInput, setShowExchangeRateInput] = useState(false);
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [commissionType, setCommissionType] = useState('off');
  const [commissionValue, setCommissionValue] = useState('');
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [checklistResponses, setChecklistResponses] = useState([]);

  // Fetch trade accounts with names
  useEffect(() => {
    const fetchTradeAccounts = async () => {
      try {
        const tradeAccountsRef = collection(db, "Users", userID, "tradeAccounts");
        const tradeAccountsSnapshot = await getDocs(tradeAccountsRef);
        const accounts = tradeAccountsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id, // Fallback to ID if no name
        }));
        // Ensure 'default' is included and first
        const defaultAccount = accounts.find(acc => acc.id === 'default') || { id: 'default', name: 'Default' };
        const otherAccounts = accounts.filter(acc => acc.id !== 'default');
        setTradeAccounts([defaultAccount, ...otherAccounts]);
      } catch (error) {
        console.error('Error fetching trade accounts:', error);
        setTradeAccounts([{ id: 'default', name: 'Default' }]); // Fallback
      }
    };
    fetchTradeAccounts();
  }, [userID]);

  useEffect(() => {
    const fetchTags = async () => {
      const tagCollectionRef = collection(db, "Users", userID, "tags");
      const tagSnapshot = await getDocs(tagCollectionRef);
      const fetchedTags = tagSnapshot.docs.map(doc => doc.id);
      setTags(fetchedTags);
    };
    fetchTags();
  }, [userID]);

  useEffect(() => {
    const fetchChecklist = async () => {
      const userDoc = await getDoc(doc(db, 'Users', userID));
      
      // Explicitly check if checklistEnabled is true (not truthy but exactly true)
      if (userDoc.exists() && userDoc.data().checklistEnabled === true) {
        const checklistDoc = await getDoc(doc(db, 'Users', userID, 'checklists', 'primary'));
        if (checklistDoc.exists()) {
          // Only set the checklist if it's enabled in the checklist document as well
          const checklistData = checklistDoc.data();
          if (checklistData.enabled === true) {
            setChecklist(checklistData);
            setChecklistResponses(new Array(checklistData.items.length).fill(false));
          } else {
            // If the checklist is disabled in its own document, set to null
            setChecklist(null);
          }
        }
      } else {
        // Make sure checklist is null when user has disabled it
        setChecklist(null);
      }
    };
    
    fetchChecklist();
  }, [userID]);

  const handleAddTag = async () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const tagCollectionRef = collection(db, "Users", userID, "tags");
      const tagDocRef = doc(tagCollectionRef, trimmedTag);
      await setDoc(tagDocRef, { tag: trimmedTag });
      setTags(prevTags => [...prevTags, trimmedTag]);
      setSelectedTags(prevTags => [...prevTags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleTagChange = (event, value) => {
    setSelectedTags(value);
  };

  const handleRemoveTag = async (tag) => {
    const tagCollectionRef = collection(db, "Users", userID, "tags");
    const tagDocRef = doc(tagCollectionRef, tag);
    await deleteDoc(tagDocRef);
    setTags(tags.filter(t => t !== tag));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'symbol') {
      const symbolValue = (value || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6);
      setTradeData(prevData => ({ ...prevData, [name]: symbolValue }));
      return;
    }
    setTradeData(prevData => {
      let updatedData = { ...prevData, [name]: value };
      if (name === 'sizeType' && value === 'dollar') {
        updatedData = { ...updatedData, lotSizeEntry: '' };
      }
      const baseCurrency = getBaseCurrency({ symbol: updatedData.symbol });
      const quoteCurrency = getQuoteCurrency({ symbol: updatedData.symbol });
      setShowExchangeRateInput(exchangeRateSymbols[updatedData.symbol] && baseCurrency !== 'USD' && quoteCurrency !== 'USD');
      if (updatedData.symbol.includes('USD')) setShowExchangeRateInput(false);
      if (name === 'leverage') {
        const numericValue = parseFloat(value);
        updatedData = { ...updatedData, [name]: isNaN(numericValue) ? '' : numericValue };
      }
      return updatedData;
    });
  };

  const validateForm = () => {
    if (!tradeData.symbol) {
      setModalMessage('Please select a symbol.');
      setIsModalOpen(true);
      return false;
    }
  
    if (!tradeData.entry || isNaN(parseFloat(tradeData.entry))) {
      setModalMessage('Please enter a valid entry price.');
      setIsModalOpen(true);
      return false;
    }
  
    if (!tradeData.shortLong) {
      setModalMessage('Please select Short or Long.');
      setIsModalOpen(true);
      return false;
    }
  
    if (!tradeData.status) {
      setModalMessage('Please select a trade status.');
      setIsModalOpen(true);
      return false;
    }
  
    // Validate size based on sizeType
    if (!tradeData.sizeType) {
      setModalMessage('Please select a size type (Lot or Dollar).');
      setIsModalOpen(true);
      return false;
    }
    if (tradeData.sizeType === 'lot') {
      if (!tradeData.lotSizeEntry || isNaN(parseFloat(tradeData.lotSizeEntry)) || parseFloat(tradeData.lotSizeEntry) <= 0) {
        setModalMessage('Please enter a valid positive lot size.');
        setIsModalOpen(true);
        return false;
      }
    } else if (tradeData.sizeType === 'dollar') {
      if (!tradeData.sizeAmount || isNaN(parseFloat(tradeData.sizeAmount)) || parseFloat(tradeData.sizeAmount) <= 0) {
        setModalMessage('Please enter a valid positive dollar amount.');
        setIsModalOpen(true);
        return false;
      }
    }
  
    // Prevent exit price/date for "Open" trades
    if (tradeData.status === 'Open') {
      if (tradeData.exit && tradeData.exit !== '') {
        setModalMessage('Exit price should not be set for an Open trade.');
        setIsModalOpen(true);
        return false;
      }
      if (tradeData.exitDate && tradeData.exitDate !== '') {
        setModalMessage('Exit date should not be set for an Open trade.');
        setIsModalOpen(true);
        return false;
      }
    } else if (tradeData.status === 'Closed') {
      if (!tradeData.exit || isNaN(parseFloat(tradeData.exit))) {
        setModalMessage('Please enter a valid exit price for a Closed trade.');
        setIsModalOpen(true);
        return false;
      }
    }
  
    // Validate TP direction
    if (tradeData.tp && !isNaN(parseFloat(tradeData.tp))) {
      const entry = parseFloat(tradeData.entry);
      const tp = parseFloat(tradeData.tp);
      const isShort = tradeData.shortLong.toLowerCase() === 'short';
      if (isShort && tp > entry) {
        setModalMessage('Take Profit should be below Entry for a Short trade.');
        setIsModalOpen(true);
        return false;
      }
      if (!isShort && tp < entry) {
        setModalMessage('Take Profit should be above Entry for a Long trade.');
        setIsModalOpen(true);
        return false;
      }
    }
  
    // Additional validations
    if (tradeData.sl && isNaN(parseFloat(tradeData.sl))) {
      setModalMessage('Please enter a valid Stop Loss price.');
      setIsModalOpen(true);
      return false;
    }
  
    if (showExchangeRateInput && tradeData.status === 'Closed' && tradeData.exchangeRate && isNaN(parseFloat(tradeData.exchangeRate))) {
      setModalMessage('Please enter a valid exchange rate or leave blank to auto-fetch.');
      setIsModalOpen(true);
      return false;
    }
  
    if (tradeData.date && isNaN(new Date(tradeData.date).getTime())) {
      setModalMessage('Please enter a valid entry date.');
      setIsModalOpen(true);
      return false;
    }
  
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (tradeData.status !== 'Open' && !tradeData.exit) {
      setModalMessage('Exit price is required for Closed or Missed trades.');
      setIsModalOpen(true);
      return;
    }

  // Removed the exchange rate validation here since we'll fetch it if blank
  /* if (showExchangeRateInput && tradeData.status === 'Closed' && !tradeData.exchangeRate) {
    setModalMessage('Exchange rate is required for Closed trades.');
    setIsModalOpen(true);
    return;
  } */

    if (checklist) {
      setIsChecklistModalOpen(true);
    } else {
      submitTrade();
    }
  };

  const calculateChecklistScore = () => {
    if (!checklist || !checklist.items) return 0;
    const totalScore = checklist.items.reduce((sum, item, index) => {
      return sum + (checklistResponses[index] ? parseFloat(item.weight) : 0);
    }, 0);
    return Math.round(totalScore);
  };

  const submitTrade = async () => {
    let updatedTradeData = { ...tradeData, commissionType, commissionValue }; // Sync state

    if (showExchangeRateInput && tradeData.status === 'Closed' && !tradeData.exchangeRate) {
      const exchangeRateSymbol = getExchangeRateSymbol(tradeData);
      const fetchedRate = await fetchExchangeRate(exchangeRateSymbol);
      if (fetchedRate) {
        updatedTradeData.exchangeRate = fetchedRate;
      } else {
        console.warn('Failed to fetch exchange rate; proceeding with default value');
        setModalMessage(`Couldn’t fetch ${exchangeRateSymbol} rate. Please enter it manually next time. Using default value (1).`);
        setIsModalOpen(true);
        updatedTradeData.exchangeRate = "1";
      }
    }

    const checklistScore = calculateChecklistScore();
    const fxbTradeScore = checklist ? `${checklistScore}/100` : 'N/A';

    const lotSize = parseFloat(updatedTradeData.lotSizeEntry) || 0;
    const entry = parseFloat(updatedTradeData.entry);
    const exit = parseFloat(updatedTradeData.exit);
    const direction = updatedTradeData.shortLong.toLowerCase() === 'short' ? -1 : 1;
    let percentageGain = ((exit - entry) / Math.abs(entry)) * 100 * direction;
    let returns, riskAmount, commission = 0;
    const exchangeRate = getExchangeRate(updatedTradeData);
    const quoteCurrency = getQuoteCurrency(updatedTradeData.symbol);

    if (!isNaN(entry) && !isNaN(exit)) {
      if (updatedTradeData.sizeType === 'lot' && !isNaN(lotSize)) {
        const pipDifference = (exit - entry) / calculatePipIncrement(updatedTradeData.symbol) * direction;
        if (['XAUUSD', 'US30', 'SPX500', 'NAS100', 'UK100', 'GER40', 'XAGUSD', 'XPTUSD', 'XPDUSD', 'WTIUSD', 'BRENTUSD', 'NGASUSD', 'COFFEEUSD', 'COTTONUSD'].includes(updatedTradeData.symbol)) {
          const pipAmount = Math.abs(pipDifference);
          returns = pipAmount * lotSize * Math.sign(pipDifference) / exchangeRate;
          const pipsToSL = (parseFloat(updatedTradeData.sl) - entry) / calculatePipIncrement(updatedTradeData.symbol) * direction;
          riskAmount = Math.abs(pipsToSL) * lotSize / exchangeRate;
        } else {
          const pipAmount = Math.abs(pipDifference) * 10;
          returns = pipAmount * lotSize * Math.sign(pipDifference) / exchangeRate;
          const pipsToSL = (parseFloat(updatedTradeData.sl) - entry) / calculatePipIncrement(updatedTradeData.symbol) * direction;
          riskAmount = (Math.abs(pipsToSL) * lotSize * 10) / exchangeRate;
          if (quoteCurrency === 'JPY') {
            returns *= 100;
            riskAmount *= 100;
          }
        }

        // Commission calculation (consolidated logic)
        if (commissionType === 'lotFixed' && commissionValue) {
          commission = lotSize * parseFloat(commissionValue); // e.g., $5 per lot
        } else if (commissionType === 'lotPercentage' && commissionValue) {
          const contractSize = getContractSize(updatedTradeData.symbol);
          const notionalValue = lotSize * contractSize * entry; // In quote currency
          const notionalInUSD = quoteCurrency === 'USD' ? notionalValue : notionalValue / exchangeRate;
          commission = notionalInUSD * (parseFloat(commissionValue) / 100); // e.g., 0.01%
        } else if (commissionType === 'percentage' && commissionValue && returns > 0) {
          commission = returns * (parseFloat(commissionValue) / 100); // e.g., 10% of profit
        } else if (commissionType === 'dollar' && commissionValue) {
          commission = parseFloat(commissionValue); // Flat fee
        }

        returns -= commission;
      } else if (updatedTradeData.sizeType === 'dollar') {
        const sizeAmount = parseFloat(updatedTradeData.sizeAmount) || 0;
        const leverage = parseFloat(updatedTradeData.leverage) || 1;
        const profitLoss = exit - entry;
        percentageGain = (profitLoss / entry) * 100 * leverage;
        returns = (profitLoss / entry) * sizeAmount * leverage * direction;
        const moveToSL = direction === 1 ? parseFloat(updatedTradeData.sl) - entry : entry - parseFloat(updatedTradeData.sl);
        riskAmount = (Math.abs(moveToSL) / entry) * sizeAmount * leverage;
        if (direction === -1) percentageGain *= -1;

        if (commissionType === 'percentage' && commissionValue && returns > 0) {
          commission = returns * (parseFloat(commissionValue) / 100);
        } else if (commissionType === 'dollar' && commissionValue) {
          commission = parseFloat(commissionValue);
        }
        returns -= commission;
      }
    }

    let tradeToAdd = {
      ...updatedTradeData,
      userID,
      date: updatedTradeData.date,
      tags: selectedTags,
      commission: commissionType === 'off' ? 'N/A' : `$${commission.toFixed(2)}`, // Always use $ for non-'off' commissions
      checklistResponses: checklist ? checklistResponses : null,
      fxbTradeScore,
      returns: returns || 0,
      returnPercentage: percentageGain || 0,
      riskAmount: riskAmount || 0,
    };

    try {
      const tradesPath = `Users/${userID}/tradeAccounts/${updatedTradeData.tradeAccountID}/trades`;
      const docRef = await addDoc(collection(db, tradesPath), tradeToAdd);
      console.log('Trade data added with ID:', docRef.id);
      onTradeSubmit(tradeToAdd); // Notify parent component (Tables)
      setTradeData({
        date: new Date().toISOString().split('T')[0],
        entryTime: '',
        exitDate: '',
        exitTime: '',
        symbol: '',
        entry: '',
        exit: '',
        status: 'Open',
        sizeType: 'lot',
        lotSizeEntry: '',
        shortLong: 'Long',
        returns: '',
        returnPercentage: '',
        leverage: '',
        sl: '',
        tp: '',
        riskAmount: '',
        comments: '',
        baseCurrency: '',
        exchangeRate: '',
        tradeConfidence: 'Medium',
        tags: [],
        commissionType: 'off',
        commissionValue: '',
        tradeAccountID: 'default', // Reset to default
      });
      setSelectedTags([]);
      setCommissionType('off');
      setCommissionValue('');
      setIsModalOpen(false);
      setIsChecklistModalOpen(false);
    } catch (error) {
      console.error('Error adding trade data:', error);
      setModalMessage('Failed to save trade. Please try again.');
      setIsModalOpen(true);
    }
  };

  const handleChecklistSubmit = () => {
    submitTrade();
  };

  return (
    <Container>
      <MDButton type="submit" variant="contained" color="warning" fullWidth onClick={() => setIsModalOpen(true)}>
        Manually Input New Trade Entry
      </MDButton>
      {isModalOpen && (
        <Paper style={{ padding: 16, backgroundColor: theme.palette.background.default || '#bac7db' }}>
          <MDTypography variant="h6" component="h2" style={{ fontWeight: 'bold', textAlign: 'center', marginRight: '20px' }}>
            Trade Entry Form
          </MDTypography>
          <form onSubmit={handleSubmit} style={{ width: '100%', overflow: 'hidden' }}>
            <Grid container spacing={2}>

              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="tags">Tags</InputLabel>
                <Autocomplete
                  multiple
                  freeSolo
                  options={tags}
                  value={selectedTags}
                  onChange={handleTagChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or add tags"
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      InputProps={{ ...params.InputProps, endAdornment: <CloseIcon onClick={() => handleRemoveTag(tagInput)} /> }}
                    />
                  )}
                />
                {selectedTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    color={theme.palette.mode === 'dark' ? 'default' : 'info'}
                    sx={{ margin: '2px' }}
                  />
                ))}
                <MDButton onClick={handleAddTag} variant="contained" color="warning" style={{ marginTop: '10px', marginLeft: '10px' }}>
                  Add New Tag
                </MDButton>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" style={{ marginTop: '20px' }}>
                  <InputLabel id="commission-type-label">Commission Type</InputLabel>
                  <Select
                    labelId="commission-type-label"
                    value={commissionType}
                    onChange={(e) => setCommissionType(e.target.value)}
                    label="Commission Type"
                  >
                    <MenuItem value="off">Commissions Fees Off</MenuItem>
                    <MenuItem value="percentage">Profit Share Percentage</MenuItem>
                    <MenuItem value="dollar">Flat Fee In Dollars</MenuItem>
                    <MenuItem value="lotFixed">Fixed Fee Per Lot ($)</MenuItem>
                    <MenuItem value="lotPercentage">Percentage Fee Per Lot (%)</MenuItem>
                  </Select>
                </FormControl>
                {commissionType !== 'off' && (
                  <TextField
                    label={
                      commissionType === 'percentage' ? "Enter Broker Profit Share Percentage (e.g., 10%)" :
                      commissionType === 'dollar' ? "Enter Flat Fee Value ($)" :
                      commissionType === 'lotFixed' ? "Enter Fixed Fee Per Lot ($)" :
                      "Enter Percentage Fee Per Lot (e.g., 0.01)"
                    }
                    value={commissionValue}
                    onChange={(e) => setCommissionValue(e.target.value)}
                    type="number"
                    fullWidth
                    style={{ marginTop: '20px' }}
                    inputProps={{ step: commissionType === 'lotPercentage' ? '0.01' : '0.1', min: 0 }}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel style={{ textAlign: 'center' }}>Entry Date</InputLabel>
                <MDInput
                  fullWidth
                  variant="outlined"
                  type="date"
                  name="date"
                  value={tradeData.date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel style={{ textAlign: 'center' }}>Entry Time</InputLabel>
                <MDInput
                  fullWidth
                  variant="outlined"
                  type="time"
                  name="entryTime"
                  value={tradeData.entryTime}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel style={{ textAlign: 'center' }}>Exit Date</InputLabel>
                <MDInput
                  fullWidth
                  variant="outlined"
                  type="date"
                  name="exitDate"
                  value={tradeData.exitDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel style={{ textAlign: 'center' }}>Exit Time</InputLabel>
                <MDInput
                  fullWidth
                  variant="outlined"
                  type="time"
                  name="exitTime"
                  value={tradeData.exitTime}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel>Symbol</InputLabel>
                <Autocomplete
                  options={symbolOptions}
                  getOptionLabel={(option) => option}
                  value={tradeData.symbol || ''}
                  onChange={(event, newValue) => handleChange({ target: { name: 'symbol', value: newValue || '' } })}
                  renderInput={(params) => (
                    <MDInput
                      {...params}
                      fullWidth
                      label="e.g. TSLA or GBPUSD"
                      variant="outlined"
                      inputProps={{ ...params.inputProps, maxLength: 6 }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option === value}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel style={{ width: '100%', marginTop: '20px', textAlign: 'left' }}>Short/Long</InputLabel>
                  <Select
                    value={tradeData.shortLong}
                    onChange={handleChange}
                    name="shortLong"
                    label="Short/Long"
                    style={{ width: '100%', marginTop: '19px' }}
                  >
                    <MenuItem value="Long">Long</MenuItem>
                    <MenuItem value="Short">Short</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel style={{ width: '100%', marginTop: '20px', textAlign: 'left' }}>Size Position Type</InputLabel>
                  <Select
                    value={tradeData.sizeType}
                    onChange={handleChange}
                    name="sizeType"
                    label="Size Position Type"
                    style={{ width: '100%', marginTop: '19px' }}
                  >
                    <MenuItem value="lot">Lot Size</MenuItem>
                    <MenuItem value="dollar">Dollar Amount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {tradeData.sizeType === 'lot' && (
                <Grid item xs={12} sm={6}>
                  <InputLabel>Lot Size</InputLabel>
                  <MDInput
                    fullWidth
                    label="Enter the lot size"
                    name="lotSizeEntry"
                    value={tradeData.lotSizeEntry}
                    onChange={handleChange}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
              )}
              {tradeData.sizeType === 'dollar' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InputLabel>$ Dollar Amount</InputLabel>
                    <MDInput
                      fullWidth
                      label="Enter the Dollar amount"
                      name="sizeAmount"
                      value={tradeData.sizeAmount}
                      onChange={handleChange}
                      variant="outlined"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel>Leverage</InputLabel>
                    <MDInput
                      fullWidth
                      label="Amount of leverage used"
                      name="leverage"
                      value={tradeData.leverage}
                      onChange={handleChange}
                      variant="outlined"
                      type="number"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <InputLabel>Entry</InputLabel>
                <MDInput
                  fullWidth
                  label="Enter the entry price"
                  name="entry"
                  value={tradeData.entry}
                  onChange={handleChange}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel>Exit</InputLabel>
                <MDInput
                  fullWidth
                  label="Enter the exit price"
                  name="exit"
                  value={tradeData.exit}
                  onChange={handleChange}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel>Stop Loss</InputLabel>
                <MDInput
                  fullWidth
                  label="Enter your stop loss limit"
                  name="sl"
                  value={tradeData.sl}
                  onChange={handleChange}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel>Take Profit</InputLabel>
                <MDInput
                  fullWidth
                  label="Enter your take profit limit"
                  name="tp"
                  value={tradeData.tp}
                  onChange={handleChange}
                  variant="outlined"
                  type="number"
                />
              </Grid>
              {showExchangeRateInput && (
                <Grid item xs={12} sm={6}>
                  <InputLabel>Exchange Rate</InputLabel>
                  <MDInput
                    fullWidth
                    label={`Leave blank to auto-fetch ${exchangeRateSymbols[tradeData.symbol]}, or enter manually`}
                    name="exchangeRate"
                    value={tradeData.exchangeRate}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <InputLabel>Comments</InputLabel>
                <MDInput
                  fullWidth
                  label="Enter any comments"
                  name="comments"
                  value={tradeData.comments}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel style={{ width: '100%', marginTop: '20px', textAlign: 'left' }}>Status</InputLabel>
                  <Select
                    value={tradeData.status}
                    onChange={handleChange}
                    name="status"
                    label="Status"
                    style={{ width: '100%', marginTop: '19px' }}
                  >
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                    <MenuItem value="Missed">Missed</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel style={{ marginTop: '20px', textAlign: 'left' }}>Trade Confidence</InputLabel>
                  <Select
                    value={tradeData.tradeConfidence}
                    onChange={handleChange}
                    name="tradeConfidence"
                    label="Trade Confidence"
                    style={{ marginTop: '19px' }}
                    MenuProps={{ MenuListProps: { style: { padding: 0 } } }}
                  >
                    <MenuItem value="High" style={{ display: 'flex', alignItems: 'center' }}>
                      <SentimentVerySatisfiedIcon style={{ color: 'green', marginTop: '10px', marginRight: '8px' }} />
                      High
                    </MenuItem>
                    <MenuItem value="Medium" style={{ display: 'flex', alignItems: 'center' }}>
                      <SentimentSatisfiedIcon style={{ color: 'orange', marginTop: '10px', marginRight: '8px' }} />
                      Medium
                    </MenuItem>
                    <MenuItem value="Low" style={{ display: 'flex', alignItems: 'center' }}>
                      <SentimentDissatisfiedIcon style={{ color: 'red', marginTop: '10px', marginRight: '8px' }} />
                      Low
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel style={{ width: '100%', marginTop: '20px', textAlign: 'left' }}>Trade Account</InputLabel>
                  <Select
                    value={tradeData.tradeAccountID}
                    onChange={handleChange}
                    name="tradeAccountID"
                    label="Trade Account"
                    style={{ width: '100%', marginTop: '19px' }}
                  >
                    {tradeAccounts.map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <MDBox display="flex" justifyContent="space-between" mt={2}>

              <MDButton variant="outlined" color="error" onClick={() => setIsModalOpen(false)}>
                Cancel
              </MDButton>
              <MDButton type="submit" variant="contained" color="success">
                Submit
              </MDButton>
            </MDBox>
          </form>
          {modalMessage && <MDTypography color="error" mt={2}>{modalMessage}</MDTypography>}
        </Paper>
      )}
      <Modal isOpen={isChecklistModalOpen && !!checklist} onClose={() => setIsChecklistModalOpen(false)} title={`Trade Checklist: ${checklist?.name || ''}`}>
        <MDBox mt={2}>
          {checklist?.items.map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={checklistResponses[index]}
                  onChange={(e) => {
                    const newResponses = [...checklistResponses];
                    newResponses[index] = e.target.checked;
                    setChecklistResponses(newResponses);
                  }}
                />
              }
              label={`${item.description} (${item.weight}%)`}
            />
          ))}
        </MDBox>
        <MDTypography
          variant="body2"
          mt={2}
          color={
            calculateChecklistScore() > 79
              ? 'success'
              : calculateChecklistScore() >= 49
              ? 'warning'
              : 'error'
          }
        >
          Current FXB Trade Score: {calculateChecklistScore()}/100
        </MDTypography>
        <MDBox display="flex" justifyContent="space-between" mt={2}>
          <MDButton variant="contained" color="success" onClick={handleChecklistSubmit}>
            Submit Trade
          </MDButton>
          <MDButton variant="outlined" color="error" onClick={() => setIsChecklistModalOpen(false)}>
            Cancel
          </MDButton>
        </MDBox>
      </Modal>
    </Container>
  );
};

TradeEntryForm.propTypes = {
  userID: PropTypes.string.isRequired,
  onTradeSubmit: PropTypes.func.isRequired, // Added to notify parent
};

export { fetchExchangeRate };
export default TradeEntryForm;